import React, { memo, useCallback } from 'react'
import { Form, Formik } from 'formik'
import { SectionCard } from 'src/components/shared/SectionCard'
import { SupplementalDataType } from 'src/constants/oatfi-business/enums'
import type { SupplementalDataFormValues } from './supplemental-data-form/SupplementalDataForm.interface'
import { SupplementalDataFormContent } from './supplemental-data-form/SupplementalDataFormContent'
import { supplementalDataFormValidator } from './supplemental-data-form/SupplementalDataForm.validator'

interface Props {
  /** Incremented by the page after a successful upload; remounts the form when it changes. */
  resetKey: number
  isUploading: boolean
  onUpload(input: { type: SupplementalDataType; invoiceId?: string; file: File }): void
}

const INITIAL_VALUES: SupplementalDataFormValues = {
  type: '',
  invoiceId: '',
  file: null,
}

// No history is shown because none exists: the upload is a pass-through to the lender with no
// entity, repository, or GET endpoint in para-node for supplemental data. Do not add a
// "documents provided" list, count, or checkmark here — there is nothing to read that state
// from, and inventing it would be fabricated content.
const SUBTITLE =
  'Send a document to the lender for this customer. Documents are not listed back here — there is no endpoint that returns what has already been provided.'

const SupplementalDataCardComponent = ({ resetKey, isUploading, onUpload }: Props) => {
  const handleSubmit = useCallback(
    (values: SupplementalDataFormValues) => {
      // The validator guarantees both before submit; this narrows the types for the payload.
      if (values.type === '' || values.file === null) {
        return
      }

      onUpload({
        type: values.type,
        // @ValidateIf on the backend applies @IsUUID only for the INVOICE type, so invoiceId is
        // sent only there.
        invoiceId:
          values.type === SupplementalDataType.Invoice ? values.invoiceId.trim() : undefined,
        file: values.file,
      })
    },
    [onUpload],
  )

  return (
    <SectionCard
      title="Supporting documents"
      subtitle={SUBTITLE}
      dataTestId="admin-customer-supplemental-data-card"
    >
      {/* `key={resetKey}` remounts the whole form after a successful upload. That clears the
          Formik values, the touched flags, AND the uncontrolled file input's DOM value in one
          move — clearing only the values would leave the previous filename on screen, and
          re-picking that same file fires no change event, so the admin could not retry it. */}
      <Formik<SupplementalDataFormValues>
        key={resetKey}
        initialValues={INITIAL_VALUES}
        validationSchema={supplementalDataFormValidator}
        onSubmit={handleSubmit}
      >
        <Form>
          <SupplementalDataFormContent isUploading={isUploading} />
        </Form>
      </Formik>
    </SectionCard>
  )
}

export const SupplementalDataCard = memo(SupplementalDataCardComponent)
